import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Target, 
  Heart, 
  Users, 
  Award,
  CheckCircle,
  Building,
  Truck,
  Shield,
  Clock,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();

  const values = [
    {
      icon: Award,
      title: language === 'en' ? 'Quality Excellence' : 'التميز في الجودة',
      description: language === 'en' 
        ? 'We source only the highest quality materials from trusted manufacturers worldwide.'
        : 'نحن نحصل فقط على أجود المواد من الشركات المصنعة الموثوقة في جميع أنحاء العالم.'
    },
    {
      icon: Heart,
      title: language === 'en' ? 'Customer First' : 'العميل أولاً',
      description: language === 'en' 
        ? 'Your success is our success. We go above and beyond to exceed your expectations.'
        : 'نجاحك هو نجاحنا. نحن نبذل قصارى جهدنا لتجاوز توقعاتك.'
    },
    {
      icon: Shield,
      title: language === 'en' ? 'Reliability & Trust' : 'الموثوقية والثقة',
      description: language === 'en' 
        ? 'Consistent delivery, competitive pricing, and unwavering commitment to our promises.'
        : 'التسليم المتسق والأسعار التنافسية والالتزام الثابت بوعودنا.'
    },
    {
      icon: Target,
      title: language === 'en' ? 'Innovation' : 'الابتكار',
      description: language === 'en' 
        ? 'Embracing new technologies and sustainable practices for a better future.'
        : 'تبني التقنيات الجديدة والممارسات المستدامة من أجل مستقبل أفضل.'
    }
  ];

  const achievements = [
    { number: '500+', label: language === 'en' ? 'Projects Delivered' : 'مشروع منجز' },
    { number: '1000+', label: language === 'en' ? 'Satisfied Clients' : 'عميل راض' },
    { number: '15+', label: language === 'en' ? 'Years of Excellence' : 'سنة من التميز' },
    { number: '50+', label: language === 'en' ? 'Expert Team Members' : 'عضو فريق خبير' },
  ];

  const certifications = [
    {
      title: language === 'en' ? 'ISO 9001:2015' : 'آيزو 9001:2015',
      description: language === 'en' ? 'Quality Management System' : 'نظام إدارة الجودة'
    },
    {
      title: language === 'en' ? 'UAE Municipal Approved' : 'معتمد من بلدية الإمارات',
      description: language === 'en' ? 'Licensed Building Materials Supplier' : 'مورد مواد بناء مرخص'
    },
    {
      title: language === 'en' ? 'Green Building Certified' : 'معتمد للمباني الخضراء',
      description: language === 'en' ? 'Sustainable Construction Materials' : 'مواد بناء مستدامة'
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className={`${isRTL ? 'font-arabic text-right lg:text-right' : 'text-left'}`}
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                {t('about.title')}
              </h1>
              <p className="text-xl text-yellow-300 mb-6 font-semibold">
                {t('about.subtitle')}
              </p>
              <p className="text-lg text-gray-200 leading-relaxed">
                {language === 'en' 
                  ? 'With over 15 years of experience in the UAE construction industry, Noor Altayseer has established itself as the premier supplier of building materials and construction solutions. We pride ourselves on delivering excellence, reliability, and innovation to every project we support.'
                  : 'مع أكثر من 15 عامًا من الخبرة في صناعة البناء في دولة الإمارات، أسست نور التيسير نفسها كمورد رائد لمواد البناء وحلول التشييد. نحن نفخر بتقديم التميز والموثوقية والابتكار لكل مشروع ندعمه.'
                }
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg"
                  alt="Noor Altayseer Team"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
              </div>
              
              {/* Floating Achievement Cards */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -left-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl"
              >
                <div className="text-2xl font-bold text-blue-600">500+</div>
                <div className="text-sm text-gray-600">
                  {language === 'en' ? 'Projects' : 'مشروع'}
                </div>
              </motion.div>
              
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl"
              >
                <div className="text-2xl font-bold text-yellow-600">15+</div>
                <div className="text-sm text-gray-600">
                  {language === 'en' ? 'Years' : 'سنة'}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className={`${isRTL ? 'font-arabic text-right' : 'text-left'}`}
            >
              <div className="flex items-center space-x-4 rtl:space-x-reverse mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{t('about.mission')}</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {t('about.missionText')}
              </p>
              
              <div className="space-y-4">
                {[
                  language === 'en' ? 'Quality-first approach in everything we do' : 'نهج الجودة أولاً في كل ما نقوم به',
                  language === 'en' ? 'Building lasting partnerships with our clients' : 'بناء شراكات دائمة مع عملائنا',
                  language === 'en' ? 'Supporting UAE\'s infrastructure development' : 'دعم تطوير البنية التحتية في دولة الإمارات'
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3 rtl:space-x-reverse"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className={`${isRTL ? 'font-arabic text-right' : 'text-left'}`}
            >
              <div className="flex items-center space-x-4 rtl:space-x-reverse mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{t('about.values')}</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {t('about.valuesText')}
              </p>
              
              <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {t('about.experience')}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.experienceText')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Our Core Values' : 'قيمنا الأساسية'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'The principles that guide every decision we make and every relationship we build'
                : 'المبادئ التي توجه كل قرار نتخذه وكل علاقة نبنيها'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`text-center group ${isRTL ? 'font-arabic' : ''}`}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'en' ? 'Our Achievements' : 'إنجازاتنا'}
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Numbers that reflect our commitment to excellence and customer satisfaction'
                : 'أرقام تعكس التزامنا بالتميز ورضا العملاء'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`text-center ${isRTL ? 'font-arabic' : ''}`}
              >
                <div className="text-4xl md:text-6xl font-bold text-yellow-400 mb-2">
                  {achievement.number}
                </div>
                <div className="text-sm md:text-base text-gray-200">
                  {achievement.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Certifications & Standards' : 'الشهادات والمعايير'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Committed to maintaining the highest industry standards and certifications'
                : 'ملتزمون بالحفاظ على أعلى معايير وشهادات الصناعة'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`text-center ${isRTL ? 'font-arabic' : ''}`}
              >
                <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {cert.title}
                  </h3>
                  <p className="text-gray-600">
                    {cert.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'en' 
                ? 'Partner with Excellence'
                : 'شارك مع التميز'
              }
            </h2>
            <p className="text-xl mb-10 text-gray-200">
              {language === 'en' 
                ? 'Experience the Noor Altayseer difference. Join hundreds of satisfied clients who trust us for their construction material needs.'
                : 'اختبر الفرق مع نور التيسير. انضم إلى المئات من العملاء الراضين الذين يثقون بنا لاحتياجاتهم من مواد البناء.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/quote"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-xl hover:shadow-yellow-500/25 hover:scale-105"
              >
                {t('nav.quote')}
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-300 shadow-xl hover:scale-105"
              >
                {t('nav.contact')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;